import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';

import {SelectItem} from 'primeng/api';

import {PageRequest} from '../../../../core';
import {TokenService} from '../../../../core/auth';
import {
  DetectionType,
  FileChecksumResponse,
  Project,
  ProjectAccess,
  ProjectBranch,
  ProjectBranchResponse,
  ProjectSecurityFeature,
  ProjectSecurityFeatureStatus,
  ReleaseBundle,
  UpdateFileChecksumRequest,
  UpdateProjectBranchRequest,
  UpdateProjectSecurityFeature, UpdateProjectSettingRequest
} from '../../../../core/models';
import {ProjectBranchService, ProjectService, ReleaseBundleService} from '../../../../core/services';

import {BreadcrumbService} from '../../../../breadcrumb.service';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {ProjectSidebarService} from '../../../../projectsidebar.service';
import {Subject} from 'rxjs/Subject';
import {Options} from 'ng5-slider';
import {DetectionSettingUpdatePreviewRequest, DetectionSettingUpdatePreviewResponse} from '../../../../core/models';

@Component({
  selector: 'app-project-protection',
  templateUrl: './project-protection.page.html',
  styleUrls: ['./project-protection.page.scss']
})
export class ProjectChecksumPage implements OnInit {
  loading = false;
  id: string;

  project: Project;
  projectOrig: Project;
  projectSecurityFeatures = {
    CHECKSUM: {
      OFF: false,
      COLLECT_DATA: false,
      DETECT_CHEATS: false
    },
    SNAPSHOT: {
      OFF: false,
      COLLECT_DATA: false,
      DETECT_CHEATS: false
    },
    HANDLE_MONITORING: {
      OFF: false,
      COLLECT_DATA: false,
      DETECT_CHEATS: false
    },
    HARDWARE_SURVEY: {
      OFF: false,
      COLLECT_DATA: false,
      DETECT_CHEATS: false
    },
    HYPERVISOR: {
      OFF: false,
      COLLECT_DATA: false,
      DETECT_CHEATS: false
    },
    DISTR_HASHING: {
      OFF: false,
      COLLECT_DATA: false,
      DETECT_CHEATS: false
    }
  };

  @Input() fileChecksumQuery = new PageRequest(1, 20);
  @Input() projectBranchesQuery = new PageRequest(1, 20);

  private searchTerms = new Subject<PageRequest>();

  fileChecksumResponse: FileChecksumResponse;
  projectBranchResponse: ProjectBranchResponse;

  projectBranches: {
    projectBranch: ProjectBranch;
    updateReleaseBundle?: ReleaseBundle;
  }[];

  releaseBundleList: SelectItem[];
  branches: SelectItem[];
  selectedBranch: ProjectBranch;
  authUser = this.token.whoAmI();
  userProjectAccess: ProjectAccess;

  displayHistory: boolean;

  selectedHistoryBranch: ProjectBranch;

  protectionSliderOptions: Options = {
    showTicks: true,
    showTicksValues: true,
    hideLimitLabels: true,
    hidePointerLabels: true,
    floor: -1,
    ceil: 2,
    stepsArray: [
      {value: -1, legend: 'Custom'},
      {value: 0, legend: 'Off'},
      {value: 1, legend: 'Collect Data'},
      {value: 2, legend: 'Detect Cheats'}
    ],
    disabled: true,
    vertical: false
  };

  protectionSliderValue: number = 0;
  readonly PROTECTION_SLIDER_CUSTOM: number = -1;
  readonly PROTECTION_SLIDER_OFF: number = 0;
  readonly PROTECTION_SLIDER_COLLECT_DATA: number = 1;
  readonly PROTECTION_SLIDER_DETECT_CHEATS: number = 2;

  strictness: SelectItem[];

  detectionSettings: any = null;
  displayDetectionSettingUpdateConfirmationDialog: boolean = false;
  detectionSettingUpdatePreview: DetectionSettingUpdatePreviewResponse;

  constructor(
    private route: ActivatedRoute,
    private token: TokenService,
    private projectService: ProjectService,
    private projectBranchService: ProjectBranchService,
    private breadcrumbService: BreadcrumbService,
    private projectSidebarService: ProjectSidebarService,
    private releaseBundleService: ReleaseBundleService
  ) {
    this.strictness = [
      {label: 'Auto', value: 'AUTO'},
      {label: 'Manual', value: 'MANUAL'}
    ];
  }

  ngOnInit() {
    this.loading = true;
    this.route.parent.params.subscribe(params => {
      this.id = params.id;
    });

    this.route.parent.data.subscribe(data => {
      if (data.project) {
        this.project = Object.assign({}, data.project);
        this.initDetectionSettings(this.project);
        this.projectOrig = Object.assign({}, data.project);
        this.userProjectAccess = Object.assign(
          {},
          data.userProjectAccess
        );
        this.initProjectSecurityFeatures();
        this.loadBranches();
        this.loadReleaseBundleVersions();
        this.breadcrumbService.setItems([
          {label: 'projects', routerLink: ['/project', 'list']},
          {label: this.project.name},
          {label: 'protection'}
        ]);

        this.projectSidebarService.setProject(this.project);
      }
      this.loading = false;
    });
  }

  initDetectionSettings(project: Project) {
    this.detectionSettings = {
      PROJECT_SETTING_TRUST_SCORE_LIMIT: project.projectSettings.filter(ps => ps.name === 'PROJECT_SETTING_TRUST_SCORE_LIMIT')[0],
      PROJECT_SETTING_SUSPICION_SCORE_LIMIT: project.projectSettings.filter(ps => ps.name === 'PROJECT_SETTING_SUSPICION_SCORE_LIMIT')[0],
      PROJECT_SETTING_DETECTION_TYPE: project.projectSettings.filter(ps => ps.name === 'PROJECT_SETTING_DETECTION_TYPE')[0]
    };
    this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value = this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value === null ? 0 : parseInt(this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value);
    this.detectionSettings.PROJECT_SETTING_TRUST_SCORE_LIMIT.value = this.detectionSettings.PROJECT_SETTING_TRUST_SCORE_LIMIT.value === null ? 0 : parseInt(this.detectionSettings.PROJECT_SETTING_TRUST_SCORE_LIMIT.value);
  }

  initProjectSecurityFeatures() {
    for (let i = 0; i < this.project.projectSecurityFeatures.length; i++) {
      for (let prop in this.projectSecurityFeatures[
        this.project.projectSecurityFeatures[i].securityFeatureType
        ]) {
        this.projectSecurityFeatures[
          this.project.projectSecurityFeatures[i].securityFeatureType
          ][prop] = false;
      }
      if (
        this.project.projectSecurityFeatures[i].status ==
        ProjectSecurityFeatureStatus.DETECT_CHEATS
      ) {
        this.projectSecurityFeatures[
          this.project.projectSecurityFeatures[i].securityFeatureType
          ][ProjectSecurityFeatureStatus.COLLECT_DATA] = true;
        this.projectSecurityFeatures[
          this.project.projectSecurityFeatures[i].securityFeatureType
          ][ProjectSecurityFeatureStatus.DETECT_CHEATS] = true;
      } else {
        this.projectSecurityFeatures[
          this.project.projectSecurityFeatures[i].securityFeatureType
          ][this.project.projectSecurityFeatures[i].status] = true;
      }
    }

    // initialize protection slider
    if (
      this.project.projectSecurityFeatures.every(
        feature => feature.status === ProjectSecurityFeatureStatus.OFF
      )
    ) {
      this.protectionSliderValue = this.PROTECTION_SLIDER_OFF;
    } else if (
      this.project.projectSecurityFeatures.every(
        feature =>
          feature.status === ProjectSecurityFeatureStatus.COLLECT_DATA
      )
    ) {
      this.protectionSliderValue = this.PROTECTION_SLIDER_COLLECT_DATA;
    } else if (
      this.project.projectSecurityFeatures.every(
        feature =>
          feature.status ===
          ProjectSecurityFeatureStatus.DETECT_CHEATS
      )
    ) {
      this.protectionSliderValue = this.PROTECTION_SLIDER_DETECT_CHEATS;
    } else {
      this.protectionSliderValue = this.PROTECTION_SLIDER_CUSTOM;
    }
    this.protectionSliderOptions.disabled = !this.authUser.verifyProjectRole(
      'manager',
      this.userProjectAccess.role
    );
  }

  onProtectionSliderChange(event: any) {
    if (event.value !== this.PROTECTION_SLIDER_CUSTOM) {
      let targetStatus =
        event.value === this.PROTECTION_SLIDER_OFF
          ? ProjectSecurityFeatureStatus.OFF
          : event.value === this.PROTECTION_SLIDER_COLLECT_DATA
          ? ProjectSecurityFeatureStatus.COLLECT_DATA
          : ProjectSecurityFeatureStatus.DETECT_CHEATS;
      // update respective properties
      for (
        let i = 0;
        i < this.project.projectSecurityFeatures.length;
        i++
      ) {
        this.project.projectSecurityFeatures[i].status = targetStatus;
      }

      // perform the actual update
      const request = new UpdateProjectSecurityFeature(
        this.project.projectSecurityFeatures
      );
      this.projectService
        .updateSecurityFeature(this.project.id, request)
        .subscribe(data => {
          this.initProjectSecurityFeatures();
        });
    } else {
      this.initProjectSecurityFeatures();
    }
  }

  saveSettings() {
    // update respective properties
    for (let i = 0; i < this.project.projectSecurityFeatures.length; i++) {
      if (
        !this.projectSecurityFeatures[
          this.project.projectSecurityFeatures[i].securityFeatureType
          ]['COLLECT_DATA']
      ) {
        this.project.projectSecurityFeatures[i].status =
          ProjectSecurityFeatureStatus.OFF;
      } else if (
        this.projectSecurityFeatures[
          this.project.projectSecurityFeatures[i].securityFeatureType
          ]['COLLECT_DATA'] &&
        !this.projectSecurityFeatures[
          this.project.projectSecurityFeatures[i].securityFeatureType
          ]['DETECT_CHEATS']
      ) {
        this.project.projectSecurityFeatures[i].status =
          ProjectSecurityFeatureStatus.COLLECT_DATA;
      } else {
        this.project.projectSecurityFeatures[i].status =
          ProjectSecurityFeatureStatus.DETECT_CHEATS;
      }
    }

    // perform the actual update
    const request = new UpdateProjectSecurityFeature(
      this.project.projectSecurityFeatures
    );
    this.projectService
      .updateSecurityFeature(this.project.id, request)
      .subscribe(data => {
        this.initProjectSecurityFeatures();
      });
  }

  updateDetectionSettings() {
    if (this.detectionSettings.PROJECT_SETTING_DETECTION_TYPE.value === 'MANUAL' && !this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value) {
      this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value = 0;
    }

    if (this.detectionSettings.PROJECT_SETTING_DETECTION_TYPE.value === 'MANUAL' && !this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value) {
      this.detectionSettings.PROJECT_SETTING_TRUST_SCORE_LIMIT.value = 0;
    }

    let updateRequests = [];
    for (let setting in this.detectionSettings) {
      updateRequests.push(new UpdateProjectSettingRequest(this.detectionSettings[setting].id, this.detectionSettings[setting].value));
    }

    this.project.projectSettings.map(s => new UpdateProjectSettingRequest(s.id, s.value));
    this.projectService.updateSettings(this.project, updateRequests).subscribe(() => {
      this.displayDetectionSettingUpdateConfirmationDialog = false;
    });
  }

  loadBranches() {
    this.branches = [];
    this.project.projectBranches.forEach(branch =>
      this.branches.push({label: branch.name, value: branch})
    );
    this.selectedBranch = this.branches[0].value;
    this.loadFileChecksumInfo(this.fileChecksumQuery);
  }

  loadFileChecksumInfo(fileChecksumQuery: PageRequest) {
    this.projectBranchService
      .getFileChecksum(this.selectedBranch.id, fileChecksumQuery)
      .subscribe(
        data => {
          this.fileChecksumResponse = data;
          delete this.loading;
        },
        error => {
          delete this.loading;
        }
      );
  }

  paginateFileChecksumTable(event) {
    this.fileChecksumQuery.page = event.first / event.rows + 1;
    this.fileChecksumQuery.sortField =
      event.sortField !== undefined ? event.sortField : 'sectionName';
    this.fileChecksumQuery.sortDir = this.fileChecksumQuery.getSortDir(
      event.sortOrder
    );
    this.loadFileChecksumInfo(this.fileChecksumQuery);
  }

  paginateProjectBranches(event) {
    this.projectBranchesQuery.page = event.first / event.rows + 1;
    this.projectBranchesQuery.sortField =
      event.sortField !== undefined ? event.sortField : 'creationDate';
    this.projectBranchesQuery.sortDir = this.projectBranchesQuery.getSortDir(
      event.sortOrder
    );
    this.projectBranchesQuery.projectId = this.project.id;
    this.loadReleaseBundleVersions(this.projectBranchesQuery);
  }

  loadReleaseBundleVersions(projectBranchesQuery?: PageRequest) {
    let query = projectBranchesQuery
      ? projectBranchesQuery
      : this.projectBranchesQuery;
    query.projectId = this.project.id;

    let loadProjectBranchesAndReleaseBundlesRequests = [];
    loadProjectBranchesAndReleaseBundlesRequests.push(
      this.releaseBundleService.getAllSelectableForProjectBranch()
    );
    loadProjectBranchesAndReleaseBundlesRequests.push(
      this.projectBranchService.search(query)
    );

    this.releaseBundleList = [];
    this.projectBranches = [];

    forkJoin(loadProjectBranchesAndReleaseBundlesRequests).subscribe(
      results => {
        results['0'].forEach((releaseBundle: ReleaseBundle) => {
          let rb = Object.assign(new ReleaseBundle(), releaseBundle);
          this.releaseBundleList.push({
            label: rb.getVersion() + ' (' + rb.getStatus() + ')',
            value: rb
          });
        });

        this.projectBranchResponse = results['1'];
        results['1'].content.forEach((branch: ProjectBranch) => {
          this.projectBranches.push({
            projectBranch: branch,
            updateReleaseBundle: null
          });
        });
      },
      error => {
        delete this.loading;
      }
    );
  }

  switchBranch(newBranch: any) {
    if (newBranch != null) {
      this.selectedBranch = newBranch.value;
      this.loadFileChecksumInfo(this.fileChecksumQuery);
    }
  }

  saveProtectionSettings() {
    const updateFileChecksumRequest = new UpdateFileChecksumRequest(
      this.fileChecksumResponse.content
    );
    this.projectBranchService
      .saveFileChecksumSettings(
        this.selectedBranch.id,
        updateFileChecksumRequest
      )
      .subscribe(data => {
      });
  }

  onBranchReleaseBundleChange(event: any, projectBranch: ProjectBranch) {
    const updatedProjectBranches = this.projectBranches.filter(
      branch => projectBranch.id === branch.projectBranch.id
    );
    if (updatedProjectBranches.length === 1) {
      updatedProjectBranches[0].updateReleaseBundle = event.value;
    } else {
      console.error(
        'Found ' +
        updatedProjectBranches.length +
        ' project-branches with id ' +
        projectBranch.id
      );
    }
  }

  updateProjectBranchReleaseBundles() {
    let updateProjectBranchReleaseBundlesRequests = [];
    this.projectBranches.forEach(branch => {
      if (branch.updateReleaseBundle) {
        let updateProjectBranchRequest = new UpdateProjectBranchRequest(
          branch.projectBranch.name,
          branch.projectBranch.profilerSnapshotCount,
          branch.projectBranch.profilerDelay,
          branch.updateReleaseBundle
        );
        updateProjectBranchReleaseBundlesRequests.push(
          this.projectBranchService.updateProjectBranch(
            branch.projectBranch.id,
            updateProjectBranchRequest
          )
        );
      }
    });

    forkJoin(updateProjectBranchReleaseBundlesRequests).subscribe(
      results => {
        this.loadReleaseBundleVersions();
      },
      error => {
        this.loadReleaseBundleVersions();
      }
    );
  }

  onReleaseBundleChanged(event: ReleaseBundle) {
    let selectedBranch = this.projectBranchResponse.content.filter(
      branch => branch.id === this.selectedHistoryBranch.id
    )[0];
    selectedBranch.releaseBundle = this.releaseBundleList.filter(
      bundle => bundle.value.id === event.id
    )[0].value;
    this.onBranchReleaseBundleChange({value: event}, selectedBranch);
    this.displayHistory = false;
  }

  saveBinaryProtection() {
    const request = this.projectOrig;

    request.binaryProtection = this.project.binaryProtection;

    this.projectService.update(request).subscribe(data => {
      this.project = data;
    });
  }

  showHistory(projectBranch: ProjectBranch) {
    this.displayHistory = true;
    this.selectedHistoryBranch = projectBranch;
  }

  setAfterChange(event: ReleaseBundle) {
    this.selectedHistoryBranch.releaseBundle = this.releaseBundleList.filter(
      bundle => bundle.value.id === event.id
    )[0].value;
    const updatedProjectBranches = this.projectBranches.filter(
      branch => this.selectedHistoryBranch.id === branch.projectBranch.id
    );

    updatedProjectBranches[0].updateReleaseBundle = this.selectedHistoryBranch.releaseBundle;
  }

  getSettingModel(setting: string): ProjectSecurityFeature {
    return this.project.projectSecurityFeatures.find(
      feat => feat.securityFeatureType === setting
    );
  }

  clearDetection() {
    if (this.detectionSettings.PROJECT_SETTING_DETECTION_TYPE.value === DetectionType.AUTO) {
      this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value = null;
      this.detectionSettings.PROJECT_SETTING_TRUST_SCORE_LIMIT.value = null;
    } else {
      this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value = 0;
      this.detectionSettings.PROJECT_SETTING_TRUST_SCORE_LIMIT.value = 0;
    }
  }

  hideDetectionSettingUpdateConfirmationDialog() {
    this.displayDetectionSettingUpdateConfirmationDialog = false;
  }

  showDetectionSettingUpdateConfirmationDialog() {
    this.detectionSettingUpdatePreview = null;
    this.displayDetectionSettingUpdateConfirmationDialog = true;

    let newDetectionSettings = new DetectionSettingUpdatePreviewRequest();
    newDetectionSettings.suspicionScoreLimit = this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value ? this.detectionSettings.PROJECT_SETTING_SUSPICION_SCORE_LIMIT.value : 0;
    newDetectionSettings.trustScoreLimit = this.detectionSettings.PROJECT_SETTING_TRUST_SCORE_LIMIT.value ? this.detectionSettings.PROJECT_SETTING_TRUST_SCORE_LIMIT.value : 0;
    newDetectionSettings.detectionType = this.detectionSettings.PROJECT_SETTING_DETECTION_TYPE.value;

    this.projectService.getDetectionSettingUpdatePreview(this.project.id, newDetectionSettings).subscribe(
      (data) => {
        this.detectionSettingUpdatePreview = data;
      }
    );
  }

}
